import { type FC, forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { milisecondsToTime } from '@/common/lib/timer';
import { H3, StringXSmall } from '@/components/Typography/Typography';

import { defaultPattern } from './config';

import css from './styles.module.scss';

interface ShortTimerProps extends HTMLAttributes<HTMLDivElement> {
  timeLeft: number;
  pattern?: { text: string; key: string }[];
}

const ShortTimerUi: FC<ShortTimerProps> = forwardRef<
  HTMLDivElement,
  ShortTimerProps
>(({ timeLeft, pattern = defaultPattern, ...props }, ref) => {
  const { t } = useTranslation();
  const timeObj = milisecondsToTime(timeLeft * 1000);

  return (
    <div ref={ref} {...props} className={cn(css.shortTimer, props.className)}>
      {pattern.map(({ key, text }, index) => (
        <div
          className={cn(
            'flex flex-col items-center h-full justify-between',
            text && 'w-12',
          )}
          key={index}
        >
          <H3 weight="bold">
            {text ? timeObj[key as keyof typeof timeObj] : ':'}
          </H3>
          {text && (
            <StringXSmall className="!text-white/60">{t(text)}</StringXSmall>
          )}
        </div>
      ))}
    </div>
  );
});

const withTimerLogic = (
  Component: FC<ShortTimerProps>,
): FC<ShortTimerProps> => {
  return (props: ShortTimerProps) => {
    const timeLeft = useCountDownTimer(props.timeLeft);

    return <Component {...props} timeLeft={timeLeft} />;
  };
};

const ShortTimer = withTimerLogic(ShortTimerUi);

export { ShortTimer, ShortTimerUi };
